/* eslint-disable no-console */
import { ApolloClient, ApolloLink, HttpLink, InMemoryCache } from '@apollo/client';
import { BatchHttpLink } from '@apollo/client/link/batch-http';
import { onError } from '@apollo/client/link/error';
import { RetryLink } from '@apollo/client/link/retry';
import { CachePersistor } from 'apollo3-cache-persist';
import localForage from 'localforage';
import { loadErrorMessages, loadDevMessages } from '@apollo/client/dev';
import omitDeep from '../utils/omitDeep';
import { clearAdminImpersonating } from '../utils/impersonation';
import { markSessionInvalidated, sessionInvalidatedVar } from './sessionInvalidation';
import shouldRetryOperation from './shouldRetryOperation';
import isAdminOperation from './isAdminOperation';

if (process.env.NODE_ENV !== 'production') {
  // Adds messages only in a dev environment
  loadDevMessages();
  loadErrorMessages();
}

const cache = new InMemoryCache({
  typePolicies: {
    Query: {
      fields: {
        episodeSocialClips: {
          keyArgs: ['episodeId'],
          merge: (_existing, incoming = []) => incoming,
        },
      },
    },
    Episode: {
      fields: {
        timeline: {
          // replace the existing timeline with the incoming one when it changes
          // (if we don't define this explicitly, we get a warning in console)
          merge: (_existing, incoming = []) => incoming,
        },
      },
    },
    EpisodeFile: {
      keyFields: ['uniqueId'],
      fields: {
        audioTimeline: {
          // replace the existing audioTimeline with the incoming one when it changes
          // (if we don't define this explicitly, we get a warning in console)
          merge: (_existing, incoming) => incoming,
        },
        additionalFiles: {
          // replace the existing array with the incoming one when it changes
          // (if we don't define this explicitly, we get a warning in console)
          merge: (_existing, incoming = []) => incoming,
        },
      },
    },
    EpisodeMetaData: {
      merge: (existing = {}, incoming = {}) => ({ ...existing, ...incoming }),
    },
    EpisodeSocialClip: {
      keyFields: ['id'],
    },
    SilenceItem: {
      // The ID of a SilenceItem is unique only within the context of the EpisodeFile
      // it belongs to, so we need to disable normalization for this type.
      // This avoids a problem when splitting a clip, where the two output
      // clips from the split would try to reference the same SilenceItem
      // in the Apollo cache.
      keyFields: false,
    },
    RemovedAudioItem: {
      // See comment for SilenceItem
      keyFields: false,
    },
    AudioItem: {
      // See comment for SilenceItem
      keyFields: false,
    },
    MuteItem: {
      // See comment for SilenceItem
      keyFields: false,
    },
    BleepItem: {
      // See comment for SilenceItem
      keyFields: false,
    },
    BackgroundItem: {
      // See comment for SilenceItem
      // This would become a problem when e.g. splitting a clip
      keyFields: false,
    },
    AdditionalFile: {
      // Don't normalise AdditionalFile - keep it stored under the respective EpisodeFile
      keyFields: false,
    },
    Podcast: {
      fields: {
        processingSettings: {
          // replace the existing object with the incoming ones when they change
          // (if we don't define this explicitly, we get a warning in console)
          merge: (_existing, incoming = []) => incoming,
        },
        categories: {
          merge: (_existing, incoming = []) => incoming,
        },
      },
    },
  },
});
const newPersistor = new CachePersistor({
  cache,
  storage: localForage,
  trigger: 'write',
});
await newPersistor.restore();
export const persistor = newPersistor;

const httpLink = new HttpLink({
  uri: GQL_API_URL,
  credentials: 'include',
  fetch: (...args: any[]) => {
    // @ts-ignore
    return window.fetch(...args);
  },
});

const adminHttpLink = new HttpLink({
  uri: `${BASE_URL_BACKEND}admin-graphql`,
  credentials: 'include',
});

const batchHttpLink = new BatchHttpLink({
  uri: GQL_API_URL,
  credentials: 'include',
  batchMax: 10, // Max operations per batch (default: 10)
  batchInterval: 10, // Wait 10ms to collect operations (default: 10)
  fetch: (...args: Parameters<typeof fetch>) => {
    if (process.env.NODE_ENV !== 'production') {
      const [, options] = args;
      const body = JSON.parse(options?.body as string);
      if (Array.isArray(body) && body.length > 1) {
        console.debug(
          `[GraphQL Batch] ${body.length} operations:`,
          body.map((op: { operationName: string }) => op.operationName),
        );
      }
    }
    return fetch(...args);
  },
});

const nonBatchedOperationNames = new Set(['getPodcastStats']);

export const client = new ApolloClient({
  cache,
  link: ApolloLink.from([
    new ApolloLink((operation, forward) => {
      // remove all `__typename` properties from all queries
      if (operation.variables) {
        operation.variables = omitDeep(operation.variables, '__typename');
      }
      return forward(operation);
    }),
    onError(({ graphQLErrors }) => {
      if (
        graphQLErrors?.some(({ message }) => message.includes('AuthenticationError')) ||
        graphQLErrors?.some(({ extensions }) => extensions?.code === 'UNAUTHENTICATED')
      ) {
        handleSessionInvalidation().catch(error => {
          console.error('Failed to handle session invalidation', error);
        });
      }
    }),
    new RetryLink({
      attempts: {
        max: 20,
        retryIf: shouldRetryOperation,
      },
    }),
    // Disable batching in Cypress e2e tests (MSW doesn't support batched GraphQL requests)
    // MSW's graphql.query() handlers expect individual operations, but batching sends an array
    // of operations in a single POST request. Check at runtime if MSW is active.
    ApolloLink.split(
      isAdminOperation,
      adminHttpLink,
      ApolloLink.split(
        operation => !!(window as any).msw || nonBatchedOperationNames.has(operation.operationName),
        httpLink, // Use regular HTTP link for Cypress tests and slow non-batched operations
        batchHttpLink, // Use batch link for everything else in production/development
      ),
    ),
  ]),
});

async function handleSessionInvalidation() {
  // Prevent repeated cache purge loops once the app is already in a locked state.
  if (sessionInvalidatedVar()) {
    return;
  }

  markSessionInvalidated();
  await client.clearStore();
  await persistor.purge();
  clearAdminImpersonating();
}
